import * as React from "react"
import { CalendarDays } from "lucide-react"
import { ptBR } from "date-fns/locale"

import { cn } from "../../lib/utils"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

const toIso = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`

const calendarClassNames = {
  day_selected: "bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white",
  day_today: "border border-blue-300 text-blue-700",
  caption_label: "text-sm font-semibold capitalize text-slate-800",
  head_cell: "w-9 text-[0.75rem] font-medium text-slate-400",
  day: "h-9 w-9 rounded-md p-0 text-sm font-normal text-slate-700 hover:bg-blue-50 aria-selected:opacity-100",
}

// Painel de calendário no padrão visual do sistema (pt-BR, azul, Limpar/Hoje).
// `value` e `onChange` usam string "YYYY-MM-DD" ("" = sem data).
function DatePickerPanel({ value, onChange, clearable = true }) {
  const dateValue = value ? String(value).slice(0, 10) : ""
  const selected = dateValue ? new Date(`${dateValue}T12:00:00`) : undefined
  return (
    <>
      <Calendar
        mode="single"
        locale={ptBR}
        selected={selected}
        defaultMonth={selected}
        onSelect={date => date && onChange(toIso(date))}
        initialFocus
        classNames={calendarClassNames} />
      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
        {clearable ? (
          <button type="button" onClick={() => onChange("")} disabled={!dateValue} className="text-xs font-semibold text-slate-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40">Limpar</button>
        ) : <span />}
        <button type="button" onClick={() => onChange(toIso(new Date()))} className="text-xs font-semibold text-blue-600 hover:text-blue-800">Hoje</button>
      </div>
    </>
  )
}

// Substitui <input type="date">. Sem `children` mostra um campo dd/mm/aaaa;
// com `children` usa o conteúdo como gatilho (ex.: um chip).
function DatePicker({ value, onChange, placeholder = "Selecionar data", clearable = true, disabled, className, align = "start", children, onOpenChange, defaultOpen }) {
  const [open, setOpen] = React.useState(Boolean(defaultOpen))
  const dateValue = value ? String(value).slice(0, 10) : ""
  const label = dateValue ? new Date(`${dateValue}T12:00:00`).toLocaleDateString("pt-BR") : ""
  const handleOpenChange = next => { setOpen(next); onOpenChange?.(next) }
  const handleChange = next => { handleOpenChange(false); onChange?.(next) }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children ? (
          <button type="button" disabled={disabled} onClick={event => event.stopPropagation()} className={className}>{children}</button>
        ) : (
          <button type="button" disabled={disabled} onClick={event => event.stopPropagation()} className={cn("flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50", className)}>
            <span className={label ? "text-slate-800" : "text-slate-400"}>{label || placeholder}</span>
            <CalendarDays className="h-4 w-4 text-slate-400" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align={align} onClick={event => event.stopPropagation()} className="z-[100] w-auto rounded-xl border-slate-200 bg-white p-0 shadow-xl">
        <DatePickerPanel value={dateValue} onChange={handleChange} clearable={clearable} />
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker, DatePickerPanel }
